import type React from 'react';
import { memo, useCallback, useState } from 'react';

import { Table as ReactTable, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  type Column,
  type PaginationState,
  type Row,
  type RowData,
  type Table,
  type VisibilityState,
} from '@tanstack/react-table';
import cx from 'classnames';

import {
  type CellConfigInterface,
  type DragObjectItemInterface,
  type DropTypeEnum,
  type RowConfigInterface,
} from 'components/tables/dndStandardTable/types';

import CustomDragLayer from './CustomDragLayer';
import DragAndDropRow from './DragAndDropRow';
import EnhancedActions from './EnhancedActions';
import EnhancedColumnsMenu from './EnhancedColumnsMenu';
import EnhancedHeaderCell from './EnhancedHeaderCell';
import EnhancedPagination from './EnhancedPagination';
import EnhancedRow from './EnhancedRow';
import { useStyles } from './styles';
import {
  type ColumnsMenuClasses,
  defaultPaginationOptions,
  type EnhancedTableClasses,
  type MUIReactTableClasses,
  type TPaginationClasses,
} from './utils';

interface TableProps {
  id?: string;
  paginationState?: PaginationState;
  rows: Row<any>[];
  columnsVisibility?: VisibilityState;
  tableInstance: Table<any>;
  columnsToggle?: boolean;
  paginationOptions?: (number | { label: string; value: number })[];
  customClassName?: Partial<Record<EnhancedTableClasses, any>>;
  customTableClassName?: Partial<Record<MUIReactTableClasses, any>>;
  selectedActions?: React.ReactNode;
  selectedFlatRows?: Row<any>[];
  resizerOffset?: number;
  additionalButtons?: React.ReactNode;
  paginationClassNames?: Partial<Record<TPaginationClasses, string>>;
  manualPagination?: boolean;
  columnsMenuClasses?: Partial<Record<ColumnsMenuClasses, string>>;
  customColumnsMenu?: boolean;
  disableToggleColumns?: string[];
  rowConfig?: RowConfigInterface;
  cellConfig?: CellConfigInterface;
  rowStyle?: any;
  clickable?: boolean;
  hover?: boolean;
  selected?: boolean;
  isEditable?: boolean;
  isRowClickable?: (row: Row<any>) => boolean;
  customTableRowPreview?: (draggedObject: DragObjectItemInterface<any>) => React.ReactElement;
  pageChange?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, page: number) => void;
  paginationSizeChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  saveColumnsSelection?: (columns: Column<any>[]) => void;
  onCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, cellIIndex?: number) => void;
  onContextMenu?: (event?: React.MouseEvent<any>, rowItem?: RowData, cellIIndex?: number) => void;
  onDragStart?(rowItem: RowData): void;
  onDragEnd?(): void;
  onRowClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: Row<any>, rowIndex?: number) => void;
  onRowMouseOver?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, rowIndex?: number) => void;
  onRowMouseOut?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: RowData, rowIndex?: number) => void;
  onDrop?: (
    dragObject: DragObjectItemInterface<any>,
    droppedItemId: number,
    dropType: DropTypeEnum,
    dropRowId?: string,
  ) => void;
  onDragEnd?(): void;
}

function EnhancedTable({
  tableInstance,
  id,
  rows,
  paginationState,
  columnsToggle = true,
  additionalButtons,
  resizerOffset,
  selectedFlatRows,
  paginationOptions = defaultPaginationOptions,
  selectedActions,
  columnsMenuClasses,
  customClassName,
  customTableClassName,
  manualPagination = false,
  rowConfig,
  rowStyle,
  clickable,
  hover,
  selected,
  isEditable,
  isRowClickable,
  onCellClick,
  onContextMenu,
  onDragStart,
  onRowClick,
  onRowMouseOut,
  onRowMouseOver,
  customTableRowPreview,
  pageChange,
  saveColumnsSelection,
  paginationSizeChange,
  onDrop,
  onDragEnd,
  disableToggleColumns,
  paginationClassNames,
  customColumnsMenu,
}: Readonly<TableProps>) {
  const { classes } = useStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLElement>();

  const {
    getRowCount,
    getHeaderGroups,
    setPageIndex,
    setPageSize,
    getPageCount,
    getAllColumns,
    getVisibleFlatColumns,
  } = tableInstance;

  const rowsCount = getRowCount();
  const { pageIndex } = paginationState ?? {};
  const allColumns = getAllColumns();
  const visibleColumns = getVisibleFlatColumns();
  const enableColumnResizing = tableInstance.options.enableColumnResizing;
  const columnResizeDirection = tableInstance.options.columnResizeDirection;

  const handleShowColumnsMenu = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleHidePopoverMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCloseColumnsMenu = useCallback(() => {
    if (saveColumnsSelection) saveColumnsSelection(visibleColumns);
    handleHidePopoverMenu();
  }, [handleHidePopoverMenu, visibleColumns, saveColumnsSelection]);

  return (
    <>
      {(pageIndex !== undefined || columnsToggle || !!additionalButtons) && (
        <EnhancedActions
          additionalButtons={additionalButtons}
          columnsToggle={columnsToggle}
          manualPagination={manualPagination}
          numOfSelectedRows={selectedFlatRows?.length}
          pageIndex={pageIndex}
          paginationClassNames={paginationClassNames}
          paginationOptions={paginationOptions}
          paginationState={paginationState}
          rowsCount={rowsCount}
          selectedActions={selectedActions}
          onGetPageCount={getPageCount}
          onGetRowCount={getRowCount}
          onHandleShowColumnsMenu={handleShowColumnsMenu}
          onPageChange={pageChange}
          onPaginationSizeChange={paginationSizeChange}
          onSetPageIndex={setPageIndex}
          onSetPageSize={setPageSize}
        />
      )}
      {columnsToggle && (
        <EnhancedColumnsMenu
          allColumns={allColumns}
          anchorEl={anchorEl}
          classNames={columnsMenuClasses}
          customColumnsMenu={customColumnsMenu}
          disabledToggleColumns={disableToggleColumns}
          onClose={handleCloseColumnsMenu}
        />
      )}
      <TableContainer style={enableColumnResizing ? { borderLeft: '0.5px solid rgba(224, 224, 224, 1)' } : {}}>
        <ReactTable
          className={cx(classes.standardTable, customClassName?.table?.root)}
          id={id}
          size="small"
          style={enableColumnResizing ? { width: tableInstance.getTotalSize() } : {}}
        >
          <TableHead className={customTableClassName?.header}>
            {getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  let sortDirection = undefined;
                  const isSorted = header.column.getIsSorted();
                  if (isSorted === 'asc') sortDirection = 'asc';
                  if (isSorted === 'desc') sortDirection = 'desc';
                  const isLast = headerGroup.headers.length - 1 === index;

                  return (
                    <EnhancedHeaderCell
                      key={header.id}
                      columnResizeDirection={columnResizeDirection}
                      enableColumnResizing={enableColumnResizing}
                      header={header}
                      isEditable={isEditable}
                      isLast={isLast}
                      resizerOffset={resizerOffset}
                      sortDirection={sortDirection}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isDraggableRow = rowConfig?.draggableRow?.(row, index);
              const isDroppableRow = rowConfig?.droppableRow?.(row, index);
              const dragAndDropRow = isDraggableRow || isDroppableRow;

              if (!row) return null;
              if (dragAndDropRow)
                return (
                  <DragAndDropRow
                    key={row.id}
                    clickable={clickable}
                    columns={getAllColumns()}
                    customClassName={customTableClassName?.row}
                    hover={hover}
                    index={index}
                    isCustomPreview={!!customTableRowPreview}
                    resizingEnabled={enableColumnResizing}
                    row={row}
                    rowConfig={rowConfig}
                    selected={selected}
                    style={rowStyle}
                    onCellClick={onCellClick}
                    onContextMenu={onContextMenu}
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                    onDrop={onDrop}
                    onRowClick={onRowClick}
                    onRowMouseOut={onRowMouseOut}
                    onRowMouseOver={onRowMouseOver}
                  />
                );

              return (
                <EnhancedRow
                  key={row.id}
                  className={customTableClassName?.row}
                  isClickable={isRowClickable?.(row)}
                  isEditable={isEditable}
                  resizingEnabled={enableColumnResizing}
                  row={row}
                  style={rowStyle}
                  onRowClick={onRowClick}
                />
              );
            })}
          </TableBody>
        </ReactTable>
      </TableContainer>
      {pageIndex !== undefined && (
        <EnhancedPagination
          classNames={paginationClassNames}
          manualPagination={manualPagination}
          paginationOptions={paginationOptions}
          paginationState={paginationState}
          showActions={true}
          onGetPageCount={getPageCount}
          onGetRowCount={getRowCount}
          onPageChange={pageChange}
          onPaginationSizeChange={paginationSizeChange}
          onSetPageIndex={setPageIndex}
          onSetPageSize={setPageSize}
        />
      )}
      {customTableRowPreview && <CustomDragLayer renderLayer={customTableRowPreview} />}
    </>
  );
}

export default memo(EnhancedTable);
